import { memo, useMemo, useReducer } from 'react';

import { ExternalLink, SquarePen, Trash2 } from 'lucide-react';
import { isNonNullish } from 'remeda';
import { toast } from 'sonner';
import useSWR from 'swr';
import useSWRMutation from 'swr/mutation';

import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';

import { VideoFormDialog } from '../../videoFormDialog/VideoFormDialog';

import { mapDTOtoForm } from './utils';

import css from './VideoCard.module.css';

import { rewardsService } from 'v2/api/rewardsService';
import { videoService } from 'v2/api/videoService';
import { Popconfirm } from 'v2/components/popconfirm/Popconfirm';
import { Nullable } from 'v2/types';
import { VideoForm, VideoModel } from 'v2/types/VideoModel';
import { fileSrc, handleHttpError } from 'v2/utils';

interface VideoCardProps {
  video: VideoModel;
  onRevalidate: VoidFunction;
}

export const VideoCard = memo<VideoCardProps>(({ video, onRevalidate }) => {
  const [isOpen, setOpen] = useReducer((state) => !state, false);

  const { data: reward } = useSWR(isOpen && ['getRewardById', video.rewardId], rewardsService.getRewardById);

  const { trigger: triggerUpdate, isMutating } = useSWRMutation(['updateVideo', video.id], videoService.updateVideo, {
    onSuccess: () => {
      onRevalidate();
      toast.success('The video has been successfully updated');
      setOpen();
    },
    onError: handleHttpError,
  });

  const { trigger: triggerDelete } = useSWRMutation(['deleteVideo', video.id], videoService.deleteVideo, {
    onSuccess: () => {
      onRevalidate();
    },
    onError: handleHttpError,
  });

  const initialValues: Nullable<VideoForm> = useMemo(() => {
    return mapDTOtoForm(video, reward);
  }, [video, reward]);

  const handleSubmit = (values: VideoForm, resetForm: VoidFunction) => {
    triggerUpdate(values).then(() => {
      resetForm();
    });
  };

  return (
    <div className={css.videoCard}>
      <div style={{ backgroundImage: `url("${fileSrc(video.previewId)}")` }} className={css.videoCardPreviewContainer}>
        <Chip className={css.videoCardCategory} color="warning" label={video.category} size="small" />

        <div className={css.videoCardOverlay}>
          <Tooltip title="Open Video">
            <IconButton href={video.link} target="_blank">
              <ExternalLink />
            </IconButton>
          </Tooltip>
        </div>
      </div>

      <div className={css.videoCardContent}>
        <div className={css.videoCardContentInner}>
          <p className={css.videoCardName}>{video.name}</p>

          <div className={css.videoCardTiming}>
            <p>Time to receive a reward:</p>

            <Chip label={`${video.secondsToReward} s`} size="small" />
          </div>
        </div>

        <div className={css.videoCardActions}>
          <Popconfirm message="Are you sure that you want to delete this video?" onConfirm={triggerDelete}>
            <Button startIcon={<Trash2 width={18} />} variant="outlined" color="error" fullWidth>
              Delete
            </Button>
          </Popconfirm>

          <Button onClick={setOpen} startIcon={<SquarePen width={18} />} variant="outlined" color="warning">
            Edit
          </Button>

          {isNonNullish(initialValues) && (
            <VideoFormDialog
              title="Edit Video"
              isOpen={isOpen}
              onClose={setOpen}
              initialValues={initialValues}
              isSubmitting={isMutating}
              onSubmit={handleSubmit}
              isEditing
            />
          )}
        </div>
      </div>
    </div>
  );
});

VideoCard.displayName = 'VideoCard';
